import { CustomFieldSchema, DEFAULT_CHARACTER_CUSTOM_FIELDS, DEFAULT_CHARACTER_ROLE_TEMPLATES } from "./schemas/CharacterSchema.js";

export const CHARACTER_TEMPLATE_PREFIX = "character:";
export const DEFAULT_CHARACTER_ROLE = DEFAULT_CHARACTER_ROLE_TEMPLATES[0]?.optionValue || "protagonist";

const ROLE_SLUG_SET = new Set(DEFAULT_CHARACTER_ROLE_TEMPLATES.map((template) => template.optionValue));
const ROLE_LABEL_TO_SLUG_MAP = new Map(
  DEFAULT_CHARACTER_ROLE_TEMPLATES.map((template) => [template.name.toLowerCase(), template.optionValue])
);

const cloneField = (field) => CustomFieldSchema.cast({ label: field.label || "Description" });

export const slugifyCharacterRole = (label = "") => {
  const normalized = label.toString().toLowerCase().trim();
  const slug = normalized.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return slug || `role_${Math.random().toString(36).slice(2, 8)}`;
};

export const formatCharacterRoleLabel = (role = "") =>
  role
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();

export const isCharacterTemplateKey = (entityType = "") => typeof entityType === "string" && entityType.startsWith(CHARACTER_TEMPLATE_PREFIX);

export const stripCharacterTemplatePrefix = (entityType = "") => (isCharacterTemplateKey(entityType) ? entityType.slice(CHARACTER_TEMPLATE_PREFIX.length) : entityType);

export const isCharacterRoleSlug = (role = "") => ROLE_SLUG_SET.has(role);

export const isCharacterEntityType = (entityType = "") => {
  if (!entityType) return false;
  if (entityType === "character") return true;
  if (isCharacterTemplateKey(entityType)) return true;
  return ROLE_SLUG_SET.has(entityType);
};

export const normalizeCharacterRole = (role = "") => {
  if (!role) return DEFAULT_CHARACTER_ROLE;
  const [firstTokenRaw] = role.split(",").map((token) => token.trim()).filter(Boolean);
  const firstToken = firstTokenRaw || DEFAULT_CHARACTER_ROLE;

  if (firstToken === "character") return DEFAULT_CHARACTER_ROLE;
  if (isCharacterTemplateKey(firstToken)) {
    const stripped = stripCharacterTemplatePrefix(firstToken);
    return ROLE_SLUG_SET.has(stripped) ? stripped : slugifyCharacterRole(stripped);
  }
  if (ROLE_SLUG_SET.has(firstToken)) {
    return firstToken;
  }
  const labelMatch = ROLE_LABEL_TO_SLUG_MAP.get(firstToken.toLowerCase());
  if (labelMatch) {
    return labelMatch;
  }
  return slugifyCharacterRole(firstToken);
};

export const getCharacterTemplateEntityType = (roleOrKey = "") => {
  if (!roleOrKey || roleOrKey === "character") {
    return `${CHARACTER_TEMPLATE_PREFIX}${DEFAULT_CHARACTER_ROLE}`;
  }
  if (isCharacterTemplateKey(roleOrKey)) {
    return roleOrKey;
  }
  if (ROLE_SLUG_SET.has(roleOrKey)) {
    return `${CHARACTER_TEMPLATE_PREFIX}${roleOrKey}`;
  }
  const slug = slugifyCharacterRole(roleOrKey);
  return `${CHARACTER_TEMPLATE_PREFIX}${slug}`;
};

export const resolveCharacterTemplateDefaults = (roleOrKey = "") => {
  const role = normalizeCharacterRole(roleOrKey);
  const template = DEFAULT_CHARACTER_ROLE_TEMPLATES.find((t) => t.optionValue === role);
  const sourceFields = template?.defaultFields?.length ? template.defaultFields : DEFAULT_CHARACTER_CUSTOM_FIELDS;
  return sourceFields.map(cloneField);
};

export const listBuiltinCharacterTemplates = () => DEFAULT_CHARACTER_ROLE_TEMPLATES.map((template) => ({ ...template, defaultFields: template.defaultFields.map(cloneField) }));
